import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  ShieldCheck, ShieldAlert, ShieldOff, Shield, AlertTriangle, Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { getSystemTypeLabel } from '@/lib/systemTypeConstants';
import { hasChecklistTemplate } from '@/lib/checklists/registry';
import {
  calcularCobertura, tipoProximaLabel, type CoberturaResult, type TipoProxima,
} from '@/lib/coberturaRevisiones';
import { FijarPeriodoRevisionDialog } from './FijarPeriodoRevisionDialog';
import { CalendarCog } from 'lucide-react';

interface CoberturaRevisionesCardProps {
  clienteId: string;
  /** Si se pasa, restringe la cobertura a sistemas de esa sede. */
  sedeId?: string | null;
}

interface SistemaMin {
  id: string;
  tipo: string;
  ubicacion: string | null;
  cobertura_omitida: boolean;
  revision_ancla_periodo: string | null;
  revision_ancla_tipo: TipoProxima | null;
}

interface ChecklistMin {
  id: string;
  fecha: string | null;
  created_at: string;
  periodo: string | null;
  tipo_revision: string;
  estado: string;
  numero_trimestral: number | null;
  sistema_id: string | null;
  archivado: boolean;
}

interface CoberturaSistema {
  sistema: SistemaMin;
  result: CoberturaResult;
}

export const CoberturaRevisionesCard: React.FC<CoberturaRevisionesCardProps> = ({
  clienteId, sedeId,
}) => {
  const [sistemas, setSistemas] = useState<SistemaMin[]>([]);
  const [checklists, setChecklists] = useState<ChecklistMin[]>([]);
  const [loading, setLoading] = useState(true);
  const [fixingTrimestral, setFixingTrimestral] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [fijarSistema, setFijarSistema] = useState<SistemaMin | null>(null);

  const cargar = async () => {
    setLoading(true);
    let sistemasQuery = supabase
      .from('sistemas')
      .select('id, tipo, ubicacion, cliente_id, sede_id, cobertura_omitida, revision_ancla_periodo, revision_ancla_tipo' as any)
      .eq('cliente_id', clienteId)
      .eq('activo', true);
    if (sedeId) sistemasQuery = sistemasQuery.eq('sede_id', sedeId);
    const { data: sistemasData } = await sistemasQuery;

    const { data: checklistsData } = await (supabase as any)
      .from('checklists_seguridad')
      .select('id, fecha, created_at, periodo, tipo_revision, estado, numero_trimestral, sistema_id, archivado, modo')
      .eq('cliente_id', clienteId)
      .neq('tipo_revision', 'rapida')
      .neq('modo', 'elementos');

    setSistemas((sistemasData || []).map((s: any) => ({
      id: s.id,
      tipo: s.tipo,
      ubicacion: s.ubicacion,
      cobertura_omitida: !!s.cobertura_omitida,
      revision_ancla_periodo: s.revision_ancla_periodo ?? null,
      revision_ancla_tipo: (s.revision_ancla_tipo ?? null) as TipoProxima | null,
    })));
    setChecklists((checklistsData || []) as ChecklistMin[]);
    setLoading(false);
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clienteId, sedeId]);

  const coberturas: CoberturaSistema[] = useMemo(() => {
    const sistemasConChecklist = sistemas.filter(s => hasChecklistTemplate(s.tipo));
    const porSistema: Record<string, ChecklistMin[]> = {};
    checklists.forEach(c => {
      if (!c.sistema_id || c.archivado) return;
      (porSistema[c.sistema_id] ||= []).push(c);
    });
    return sistemasConChecklist.map(s => ({
      sistema: s,
      result: calcularCobertura(
        s.tipo,
        (porSistema[s.id] || []).map(c => ({
          fecha: c.fecha || c.created_at,
          periodo: c.periodo,
          tipo_revision: c.tipo_revision,
          estado: c.estado,
          numero_trimestral: c.numero_trimestral,
        })),
        s.cobertura_omitida,
        new Date(),
        s.revision_ancla_periodo
          ? { periodo: s.revision_ancla_periodo, tipo: s.revision_ancla_tipo ?? 'anual' }
          : null,
      ),
    }));
  }, [sistemas, checklists]);

  const ultimaTrimestralPorSistema = useMemo(() => {
    const m: Record<string, ChecklistMin> = {};
    const ordenados = [...checklists]
      .filter(c => !c.archivado && c.tipo_revision === 'trimestral' && (c.estado === 'completado' || c.estado === 'finalizada'))
      .sort((a, b) => ((b.periodo || b.fecha || '') as string).localeCompare((a.periodo || a.fecha || '') as string));
    for (const c of ordenados) {
      if (!c.sistema_id) continue;
      if (!m[c.sistema_id]) m[c.sistema_id] = c;
    }
    return m;
  }, [checklists]);

  const toggleOmitirCobertura = async (sistemaId: string, valor: boolean) => {
    const { error } = await (supabase as any)
      .from('sistemas')
      .update({ cobertura_omitida: valor })
      .eq('id', sistemaId);
    if (error) {
      toast.error('No se pudo actualizar la cobertura');
      return;
    }
    toast.success(valor ? 'Sistema marcado sin mantenimiento' : 'Cobertura reactivada');
    cargar();
  };

  const fijarNumeroTrimestral = async (checklistId: string, numero: number) => {
    setFixingTrimestral(checklistId);
    const { error } = await (supabase as any)
      .from('checklists_seguridad')
      .update({ numero_trimestral: numero })
      .eq('id', checklistId);
    setFixingTrimestral(null);
    if (error) {
      toast.error('No se pudo actualizar: ' + error.message);
      return;
    }
    toast.success(`Marcada como ${numero}ª trimestral`);
    cargar();
  };

  if (loading) {
    return (
      <div className="inline-flex h-11 w-11 items-center justify-center rounded-full border bg-muted/40">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (coberturas.length === 0) return null;

  const resumen = coberturas.reduce((acc, c) => {
    acc[c.result.estado] = (acc[c.result.estado] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const pendientes = (resumen.vencido || 0) + (resumen.sin_iniciar || 0) + (resumen.requiere_aclaracion || 0);
  const alDia = (resumen.al_dia || 0) + (resumen.proximo || 0);

  const tienePendientes = pendientes > 0;
  const TriggerIcon = tienePendientes ? ShieldAlert : ShieldCheck;
  const triggerText = tienePendientes
    ? `${pendientes} revisión${pendientes === 1 ? '' : 'es'} pendiente${pendientes === 1 ? '' : 's'}`
    : 'Cobertura al día';

  // Ordenar
  const orden: Record<string, number> = {
    vencido: 0, requiere_aclaracion: 1, sin_iniciar: 2, proximo: 3, al_dia: 4, omitido: 5,
  };
  const coberturasOrdenadas = [...coberturas].sort(
    (a, b) => (orden[a.result.estado] ?? 9) - (orden[b.result.estado] ?? 9),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={triggerText}
          aria-label={triggerText}
          className={`relative inline-flex h-11 w-11 items-center justify-center rounded-full shadow-md transition-all hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background ${
            tienePendientes
              ? 'bg-gradient-to-br from-red-500 to-red-600 text-white ring-red-400 shadow-red-500/40 animate-pulse'
              : 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white ring-emerald-400 shadow-emerald-500/30'
          }`}
        >
          <TriggerIcon className="h-6 w-6 drop-shadow-sm" strokeWidth={2.5} />
          {tienePendientes ? (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-white text-red-600 text-[11px] font-bold leading-5 text-center shadow ring-2 ring-red-500">
              {pendientes}
            </span>
          ) : (
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white text-emerald-600 text-[10px] font-bold leading-4 text-center shadow ring-2 ring-emerald-500">
              ✓
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(92vw,560px)] p-0">
        <div className="p-3 border-b">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <h4 className="text-sm font-semibold">Cobertura de revisiones</h4>
          </div>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {alDia > 0 && (
              <Badge className="bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30" variant="outline">
                {alDia} al día
              </Badge>
            )}
            {resumen.vencido ? (
              <Badge className="bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/30" variant="outline">
                {resumen.vencido} vencidas
              </Badge>
            ) : null}
            {resumen.requiere_aclaracion ? (
              <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30" variant="outline">
                {resumen.requiere_aclaracion} requieren aclaración
              </Badge>
            ) : null}
            {resumen.sin_iniciar ? (
              <Badge variant="outline">{resumen.sin_iniciar} sin iniciar</Badge>
            ) : null}
            {resumen.omitido ? (
              <Badge variant="outline" className="text-muted-foreground">
                {resumen.omitido} omitidos
              </Badge>
            ) : null}
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2 space-y-2">
          {coberturasOrdenadas.map(c => {
            const r = c.result;
            const Icono =
              r.estado === 'al_dia' ? ShieldCheck
              : r.estado === 'proximo' ? ShieldCheck
              : r.estado === 'vencido' ? ShieldAlert
              : r.estado === 'omitido' ? ShieldOff
              : r.estado === 'requiere_aclaracion' ? AlertTriangle
              : Shield;
            const colorIcono =
              r.estado === 'al_dia' ? 'text-green-600'
              : r.estado === 'proximo' ? 'text-amber-600'
              : r.estado === 'vencido' ? 'text-red-600'
              : r.estado === 'omitido' ? 'text-muted-foreground'
              : r.estado === 'requiere_aclaracion' ? 'text-amber-600'
              : 'text-muted-foreground';
            const textoEstado =
              r.estado === 'al_dia' ? 'Al día'
              : r.estado === 'proximo' ? `Próxima en ${Math.abs(r.diasDesfase)} días`
              : r.estado === 'vencido' ? `Vencida hace ${r.diasDesfase} días`
              : r.estado === 'omitido' ? 'Sin mantenimiento'
              : r.estado === 'requiere_aclaracion' ? 'Indica nº de trimestral (1T/2T/3T)'
              : 'Pendiente de primera revisión';
            return (
              <div key={c.sistema.id} className="flex items-start justify-between gap-2 rounded-md border p-2.5">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  <Icono className={`h-5 w-5 shrink-0 mt-0.5 ${colorIcono}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium text-sm truncate">{getSystemTypeLabel(c.sistema.tipo)}</span>
                      {c.sistema.ubicacion && (
                        <span className="text-muted-foreground text-xs truncate">· {c.sistema.ubicacion}</span>
                      )}
                    </div>
                    <Badge
                      variant="outline"
                      className={`mt-1 text-[10px] ${
                        r.estado === 'al_dia' ? 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30'
                        : r.estado === 'proximo' ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30'
                        : r.estado === 'vencido' ? 'bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/30'
                        : r.estado === 'requiere_aclaracion' ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30'
                        : ''
                      }`}
                    >
                      {textoEstado}
                    </Badge>
                    {r.estado !== 'omitido' && (
                      <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                        <div>
                          {r.ultima ? (
                            <>
                              Última: {format(r.ultima.fecha, 'MMM yyyy', { locale: es })} (
                              {r.ultima.tipo === 'trimestral' && r.ultima.numeroTrimestral
                                ? `${r.ultima.numeroTrimestral}T`
                                : r.ultima.tipo === 'trimestral'
                                  ? 'trimestral (sin nº)'
                                  : 'anual'}
                              )
                            </>
                          ) : (
                            <>Sin revisiones previas</>
                          )}
                          {r.proxima && (
                            <> · <strong className="text-foreground">Próxima: {tipoProximaLabel(r.proxima.tipo)}</strong> — {format(r.proxima.fecha, 'MMM yyyy', { locale: es })}</>
                          )}
                        </div>
                        {r.proximaAnual && (!r.proxima || r.proxima.tipo !== 'anual') && (
                          <div>Próxima Anual: {format(r.proximaAnual.fecha, 'MMM yyyy', { locale: es })}</div>
                        )}
                        {r.estado === 'requiere_aclaracion' && (() => {
                          const last = ultimaTrimestralPorSistema[c.sistema.id];
                          return (
                            <div className="space-y-1.5 mt-1.5">
                              <div className="text-amber-700 dark:text-amber-400">
                                Marca si era 1ª, 2ª o 3ª:
                              </div>
                              {last ? (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {([1, 2, 3] as const).map((n) => (
                                    <Button
                                      key={n}
                                      size="sm"
                                      variant="outline"
                                      className="h-7 px-2.5 text-xs"
                                      disabled={fixingTrimestral === last.id}
                                      onClick={(e) => { e.stopPropagation(); fijarNumeroTrimestral(last.id, n); }}
                                    >
                                      {n}T
                                    </Button>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-xs text-muted-foreground">
                                  No se encontró ninguna trimestral activa que editar.
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    title={c.sistema.revision_ancla_periodo ? 'Editar periodo fijado' : 'Fijar próxima revisión'}
                    onClick={(e) => { e.stopPropagation(); setFijarSistema(c.sistema); }}
                  >
                    <CalendarCog className={`h-4 w-4 ${c.sistema.revision_ancla_periodo ? 'text-sky-500' : 'text-muted-foreground'}`} />
                  </Button>
                  <Label htmlFor={`omitir-sis-${c.sistema.id}`} className="text-[10px] text-muted-foreground cursor-pointer">
                    Omitir
                  </Label>
                  <Switch
                    id={`omitir-sis-${c.sistema.id}`}
                    checked={c.sistema.cobertura_omitida}
                    onCheckedChange={(v) => toggleOmitirCobertura(c.sistema.id, v)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>

      {fijarSistema && (
        <FijarPeriodoRevisionDialog
          open={!!fijarSistema}
          onOpenChange={(v) => { if (!v) setFijarSistema(null); }}
          sistemaId={fijarSistema.id}
          sistemaTipo={fijarSistema.tipo}
          descripcion={fijarSistema.ubicacion ?? undefined}
          anclaPeriodo={fijarSistema.revision_ancla_periodo}
          anclaTipo={fijarSistema.revision_ancla_tipo}
          onGuardado={cargar}
        />
      )}
    </Popover>
  );
};
